// t725u: section headers ("Section" in the builders) for every form builder. Channel forms and checklist templates keep
// their flat field / item lists and store the headings beside them (`sections`, each placed before item `at`), so older
// apps that don't know about sections simply don't see them and nothing is ever counted, scored or answered. Handover
// templates and "Report an issue" forms already nest their fields in sections (FormSection); the builders use the same
// rows model (a section row, then the fields under it until the next section) for all of them.
import { fieldIdFromLabel, type FormField } from './forms';
import type { FormSection } from './scoped-forms';

export interface ListSection {
  id: string;
  title: string;
  description?: string | null;
  /** How many items come before the heading: it shows just above item `at` (the item count = at the end). */
  at: number;
}
export const LIST_SECTION_LIMITS = { sections: 30, title: 120, description: 300 } as const;
const SECTION_ID_RE = /^[a-z][a-z0-9_]{0,39}$/;

/** Builder- and server-side checks of the headings of a flat list with `itemCount` items. */
export function validateListSections(sections: unknown, itemCount: number): string[] {
  if (sections === undefined || sections === null) return [];
  if (!Array.isArray(sections)) return ['Sections must be a list'];
  const errs: string[] = [];
  if (sections.length > LIST_SECTION_LIMITS.sections) errs.push(`At most ${LIST_SECTION_LIMITS.sections} sections`);
  const ids = new Set<string>();
  sections.forEach((s: Partial<ListSection> | null, i) => {
    const n = `Section ${i + 1}${s && typeof s.title === 'string' && s.title.trim() ? ` (“${s.title.trim()}”)` : ''}`;
    if (!s || typeof s !== 'object') { errs.push(`${n}: invalid`); return; }
    if (typeof s.id !== 'string' || !SECTION_ID_RE.test(s.id)) errs.push(`${n}: invalid id`);
    else if (ids.has(s.id)) errs.push(`${n}: duplicate id`);
    if (typeof s.id === 'string') ids.add(s.id);
    if (typeof s.title !== 'string' || !s.title.trim()) errs.push(`${n}: give it a title`);
    else if (s.title.trim().length > LIST_SECTION_LIMITS.title) errs.push(`${n}: title must be at most ${LIST_SECTION_LIMITS.title} characters`);
    if (s.description != null && (typeof s.description !== 'string' || s.description.trim().length > LIST_SECTION_LIMITS.description)) errs.push(`${n}: description must be at most ${LIST_SECTION_LIMITS.description} characters`);
    if (typeof s.at !== 'number' || !Number.isInteger(s.at) || s.at < 0 || s.at > itemCount) errs.push(`${n}: invalid position`);
  });
  return errs;
}

/** Stored shape: trimmed, known keys only, in list order. Call after validateListSections. */
export function cleanListSections(sections: readonly ListSection[] | null | undefined): ListSection[] {
  return (sections ?? [])
    .map((s, i) => ({ s, i }))
    .sort((a, b) => a.s.at - b.s.at || a.i - b.i)
    .map(({ s }) => ({ id: s.id, title: s.title.trim(), ...(s.description?.trim() ? { description: s.description.trim() } : {}), at: s.at }));
}

/**
 * Keep headings beside the same items when a client that doesn't know about sections (an older app) saves a new item
 * list: each heading follows the item it sat above (by id); one whose item was removed stays at the same position.
 */
export function reanchorListSections(sections: readonly ListSection[] | null | undefined, oldItems: readonly { id?: string | null }[], newItems: readonly { id?: string | null }[]): ListSection[] {
  const pos = new Map(newItems.map((it, i) => [it.id ?? '', i]));
  return cleanListSections((sections ?? []).map(s => {
    const anchor = oldItems[s.at]?.id;
    const at = s.at >= oldItems.length ? newItems.length : anchor && pos.has(anchor) ? pos.get(anchor)! : Math.min(s.at, newItems.length);
    return { ...s, at };
  }));
}

/** A group of items under one heading (`section` null: items before the first heading). `start` = items before it. */
export interface ListGroup<T> { section: ListSection | null; items: T[]; start: number }

/** The items grouped under their headings, in order (for renderers). Headings with nothing under them are kept. */
export function groupBySections<T>(items: readonly T[], sections?: readonly ListSection[] | null): ListGroup<T>[] {
  const heads = cleanListSections((sections ?? []).filter(s => s && typeof s.title === 'string').map(s => ({ ...s, at: Math.max(0, Math.min(Number(s.at) || 0, items.length)) })));
  const out: ListGroup<T>[] = [];
  const first = heads[0]?.at ?? items.length;
  if (first > 0) out.push({ section: null, items: items.slice(0, first), start: 0 });
  heads.forEach((h, i) => {
    const end = heads[i + 1]?.at ?? items.length;
    out.push({ section: h, items: items.slice(h.at, end), start: h.at });
  });
  return out;
}

/** A channel form's flat fields as sections, for the sectioned renderers (fields before any heading get an untitled one). */
export function formSectionsOf(fields: readonly FormField[], sections?: readonly ListSection[] | null): FormSection[] {
  return groupBySections(fields, sections).map((g, i) => ({
    id: g.section?.id ?? `_lead${i}`, title: g.section?.title ?? '', description: g.section?.description ?? null, fields: g.items,
  }));
}

// ---- Builder rows ----------------------------------------------------------------------------------------------
export interface SectionRowData { id: string; title: string; description?: string | null }
export type BuilderRow<T> = { kind: 'section'; section: SectionRowData } | { kind: 'item'; item: T };

/** Flat items + headings as one list of rows. */
export function rowsOf<T>(items: readonly T[], sections?: readonly ListSection[] | null): BuilderRow<T>[] {
  const rows: BuilderRow<T>[] = [];
  for (const g of groupBySections(items, sections)) {
    if (g.section) rows.push({ kind: 'section', section: { id: g.section.id, title: g.section.title, description: g.section.description ?? null } });
    for (const item of g.items) rows.push({ kind: 'item', item });
  }
  return rows;
}

/** Rows back to flat items + headings. */
export function splitRows<T>(rows: readonly BuilderRow<T>[]): { items: T[]; sections: ListSection[] } {
  const items: T[] = []; const sections: ListSection[] = [];
  for (const r of rows) {
    if (r.kind === 'item') items.push(r.item);
    else sections.push({ id: r.section.id, title: r.section.title, ...(r.section.description ? { description: r.section.description } : {}), at: items.length });
  }
  return { items, sections };
}

/** Nested sections (handover templates, issue forms) as rows. */
export function rowsOfFormSections(sections: readonly FormSection[]): BuilderRow<FormField>[] {
  return sections.flatMap(s => [
    { kind: 'section' as const, section: { id: s.id, title: s.title, description: s.description ?? null } },
    ...(s.fields ?? []).map(item => ({ kind: 'item' as const, item })),
  ]);
}

/** Rows back to nested sections; any fields above the first section join it. */
export function formSectionsOfRows(rows: readonly BuilderRow<FormField>[]): FormSection[] {
  const out: FormSection[] = [];
  const lead: FormField[] = [];
  for (const r of rows) {
    if (r.kind === 'section') out.push({ id: r.section.id, title: r.section.title, ...(r.section.description ? { description: r.section.description } : {}), fields: [] });
    else if (out.length) out[out.length - 1].fields.push(r.item);
    else lead.push(r.item);
  }
  if (lead.length) {
    if (out.length) out[0].fields.unshift(...lead);
    else out.push({ id: 'section', title: 'Section 1', fields: lead });
  }
  return out;
}

/** How many item rows come before row `i` (its gutter number is this + 1). */
export const itemsBefore = <T>(rows: readonly BuilderRow<T>[], i: number) => rows.slice(0, i).filter(r => r.kind === 'item').length;

/** The rows of the section starting at row `i`: [i, end). */
function blockEnd<T>(rows: readonly BuilderRow<T>[], i: number): number {
  let j = i + 1;
  while (j < rows.length && rows[j].kind === 'item') j++;
  return j;
}
/** The row where the section above row `i` starts, or -1 (row `i` is above every section). */
function sectionAbove<T>(rows: readonly BuilderRow<T>[], i: number): number {
  for (let j = i - 1; j >= 0; j--) if (rows[j].kind === 'section') return j;
  return -1;
}

/**
 * Add a section after row `after` (null: at the end), titled "Section N" with a fresh id; the questions after it join
 * it. After a selected heading it goes below that whole section. Returns the rows and its index.
 */
export function insertSectionRow<T>(rows: readonly BuilderRow<T>[], after: number | null): { rows: BuilderRow<T>[]; index: number } {
  const taken = rows.flatMap(r => (r.kind === 'section' ? [r.section.id] : []));
  const n = taken.length + 1;
  const at = after == null || after >= rows.length ? rows.length : rows[after].kind === 'section' ? blockEnd(rows, after) : after + 1;
  const row: BuilderRow<T> = { kind: 'section', section: { id: fieldIdFromLabel(`Section ${n}`, taken), title: `Section ${n}`, description: null } };
  return { rows: [...rows.slice(0, at), row, ...rows.slice(at)], index: at };
}

/** Add an item after row `after` (null: at the end). */
export function insertItemRow<T>(rows: readonly BuilderRow<T>[], after: number | null, item: T): { rows: BuilderRow<T>[]; index: number } {
  const at = after == null || after >= rows.length ? rows.length : after + 1;
  return { rows: [...rows.slice(0, at), { kind: 'item', item }, ...rows.slice(at)], index: at };
}

/**
 * Can row `i` move up (-1) or down (1)? An item moves one row (across a heading it joins the next / previous section).
 * A section moves with its items, past the whole section above or below it. `nested`: every field must stay under a
 * section (handover templates, issue forms).
 */
export function canMoveRow<T>(rows: readonly BuilderRow<T>[], i: number, d: -1 | 1, nested = false): boolean {
  const r = rows[i];
  if (!r) return false;
  if (r.kind === 'item') {
    const j = i + d;
    if (j < 0 || j >= rows.length) return false;
    return !(nested && d === -1 && sectionAbove(rows, j + 1) === j && sectionAbove(rows, j) === -1);
  }
  if (d === -1) return sectionAbove(rows, i) >= 0;
  return blockEnd(rows, i) < rows.length;
}

/** Move row `i`; returns the rows and where the moved row now is (unchanged when it can't move). */
export function moveRow<T>(rows: readonly BuilderRow<T>[], i: number, d: -1 | 1, nested = false): { rows: BuilderRow<T>[]; index: number } {
  if (!canMoveRow(rows, i, d, nested)) return { rows: [...rows], index: i };
  if (rows[i].kind === 'item') {
    const n = [...rows]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x);
    return { rows: n, index: i + d };
  }
  const end = blockEnd(rows, i);
  const block = rows.slice(i, end);
  if (d === -1) {
    const p = sectionAbove(rows, i);
    return { rows: [...rows.slice(0, p), ...block, ...rows.slice(p, i), ...rows.slice(end)], index: p };
  }
  const nextEnd = blockEnd(rows, end);
  const next = rows.slice(end, nextEnd);
  return { rows: [...rows.slice(0, i), ...next, ...block, ...rows.slice(nextEnd)], index: i + next.length };
}

/** Can section row `i` be deleted? Its items join the section above; `nested` forms keep a section above every field. */
export function canRemoveSectionRow<T>(rows: readonly BuilderRow<T>[], i: number, nested = false): boolean {
  if (rows[i]?.kind !== 'section') return false;
  if (!nested) return true;
  if (sectionAbove(rows, i) >= 0) return true;
  // The first section: fine when nothing is under it and another section follows (or it's the only row).
  return blockEnd(rows, i) === i + 1 && (rows.length === 1 || i + 1 < rows.length);
}

/** Delete row `i` (a section's items stay and join the section above it). */
export const removeRow = <T>(rows: readonly BuilderRow<T>[], i: number): BuilderRow<T>[] => rows.filter((_, j) => j !== i);
